import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useAllSearchParams } from 'storfox-route-hooks'
import { prop } from 'ramda'
import { useDeepCompareEffect } from 'storfox-api-hooks'
import { useSnackbar } from 'storfox-snackbar'

import { escapeAtob, unescapeBtoa } from '~/utils'
import { DashboardLayout } from '~/components/Layouts'
import { useNotification } from '~/components/Notification'
import * as NAV from '~/constants/nav-titles'
import * as ROUTES from '~/constants/routes'
import {
  MOVEMENT_BULK_CREATE_COMPLETE,
  MOVEMENT_BULK_CREATE_STATUS,
  MOVEMENT_BULK_VALIDATE_COMPLETE,
  MOVEMENT_BULK_VALIDATE_STATUS
} from '~/constants/notification-topics'
import { useProgressCard } from '~/components/ProgressCard'
import { useGoogleEvent } from '~/components/GoogleAnalytics/GoogleAnalytics'
import useMessages from '~/hooks/useMessages'

import { useMovementImport } from '../hooks'
import MovementImport from '../components/MovementImport/MovementImport'

function MovementImportContainer () {
  const navigate = useNavigate()
  const snackbar = useSnackbar()
  const messages = useMessages()
  const movementImport = useMovementImport()
  const { payload } = useAllSearchParams()
  const { sendEvent } = useGoogleEvent()
  const progressCard = useProgressCard()

  const validateStatus = useNotification(MOVEMENT_BULK_VALIDATE_STATUS)
  const validateComplete = useNotification(MOVEMENT_BULK_VALIDATE_COMPLETE)
  const uploadComplete = useNotification(MOVEMENT_BULK_CREATE_COMPLETE)
  const uploadStatus = useNotification(MOVEMENT_BULK_CREATE_STATUS)

  const escapePayload = escapeAtob(payload)
  const key = prop('key', escapePayload)

  useDeepCompareEffect(() => {
    if (key) {
      validateStatus.subscribe(key, ({ payload }) => {
        const result = prop('result', payload)
        const validated = prop('totalValidated', result)
        const total = prop('total', result)

        progressCard.handleValidationChange({ current: validated, total })
        progressCard.handleCompletionChange({ current: 0, total })
      })

      validateComplete.subscribe(key, ({ payload }) => {
        navigate(`${ROUTES.MOVEMENT_IMPORT_PATH}?payload=${unescapeBtoa(payload)}`)

        validateComplete.setLoading(false)
      })

      uploadStatus.subscribe(key, ({ payload }) => {
        const result = prop('result', payload)
        const completed = prop('totalCreated', result)
        const total = prop('total', result)
        if (!uploadComplete.isLoading) {
          uploadComplete.setLoading(true)
          progressCard.handleValidationChange({ current: total, total })
        }
        progressCard.handleCompletionChange({ current: completed, total })
      })

      uploadComplete.subscribe(key, () => {
        navigate(ROUTES.MOVEMENT_LIST_PATH, { replace: true })
        uploadComplete.setLoading(false)
        sendEvent({ eventAction: 'Upload File Movement', eventCategory: 'Movement' })
      })
    }

    return () => {
      validateStatus.unsubscribe()
      validateComplete.unsubscribe()
      uploadStatus.unsubscribe()
      uploadComplete.unsubscribe()
    }
  }, [key])

  const handleSubmit = () => {
    uploadComplete.setLoading(true)
    return movementImport.create({ key })
      .then(() => snackbar({ message: messages.IMPORT_REQUEST_SUCCESS }))
  }

  const handleSubscribe = key => {
    snackbar({ message: messages.NOTIFICATION_WAIT })
    validateComplete.setLoading(true)
    navigate(`${ROUTES.MOVEMENT_IMPORT_PATH}?payload=${unescapeBtoa({ key })}`, { replace: true })
    sendEvent({ eventAction: 'Validate File Movement', eventCategory: 'Movement' })
  }

  const handleFileRemove = () => {
    validateComplete.unsubscribe()
    uploadComplete.unsubscribe()
    validateStatus.unsubscribe()
    uploadStatus.unsubscribe()
    progressCard.resetValues()
  }

  const title = 'Import movements'

  const isLoading = (
    movementImport.isLoading ||
    validateComplete.isLoading ||
    uploadComplete.isLoading
  )

  const breadcrumbs = { title }

  return (
    <DashboardLayout
      title={title}
      activeNav={NAV.MOVEMENT}
      breadcrumbs={breadcrumbs}
      isLoading={isLoading}
    >
      <MovementImport
        title={title}
        isLoading={isLoading}
        progressCard={progressCard}
        onSubscribe={handleSubscribe}
        onSubmit={handleSubmit}
        onFileRemove={handleFileRemove}
      />
    </DashboardLayout>
  )
}

export default MovementImportContainer
